import { useEffect, type ReactNode } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import { useBrand } from '../brand';
import { parseDatePath } from '../lib/dates';
import './seo.css';

/**
 * Search-engine plumbing for the customer SPA. index.html ships one canonical
 * link and one og:url, both for the home page; these keep them on the page
 * actually shown, and mark pages that don't exist so they are never indexed.
 */

/** A page's address for search engines: origin and path, no query, no trailing slash (bar the root). */
export function canonicalUrl(origin: string, pathname: string): string {
  const path = pathname.length > 1 ? pathname.replace(/\/+$/, '') : pathname;
  return `${origin}${path || '/'}`;
}

function headElement<T extends Element>(selector: string, create: () => T): T {
  const found = document.head.querySelector<T>(selector);
  if (found) return found;
  const el = create();
  document.head.appendChild(el);
  return el;
}

/** Keeps <link rel="canonical"> and og:url on the current route, so no page claims to be the home page. */
export function CanonicalLink() {
  const { pathname } = useLocation();
  useEffect(() => {
    const url = canonicalUrl(window.location.origin, pathname);
    headElement('link[rel="canonical"]', () => Object.assign(document.createElement('link'), { rel: 'canonical' })).href = url;
    headElement('meta[property="og:url"]', () => {
      const meta = document.createElement('meta');
      meta.setAttribute('property', 'og:url');
      return meta;
    }).content = url;
  }, [pathname]);
  return null;
}

/** Tells search engines not to index the page while it is shown; puts the site's own rule back after. */
export function useNoIndex() {
  useEffect(() => {
    const existing = document.head.querySelector<HTMLMetaElement>('meta[name="robots"]');
    const meta = existing ?? Object.assign(document.createElement('meta'), { name: 'robots' });
    const previous = existing?.content;
    meta.content = 'noindex';
    if (!existing) document.head.appendChild(meta);
    return () => {
      if (previous === undefined) meta.remove();
      else meta.content = previous;
    };
  }, []);
}

/** What an address that isn't a page shows. Never indexed. */
export function NotFoundView() {
  const brand = useBrand();
  useNoIndex();
  return (
    <section className="ui-notfound" aria-labelledby="ui-notfound-title">
      <h1 id="ui-notfound-title" className="ui-notfound-title">Page not found</h1>
      <p className="ui-notfound-text">We couldn't find that page on {brand.name}. It may have moved, or the link may be mistyped.</p>
      <div className="ui-notfound-actions">
        <Link className="ui-notfound-btn primary" to="/">Go to today's tips</Link>
        <Link className="ui-notfound-btn" to="/contact">Contact us</Link>
      </div>
    </section>
  );
}

/**
 * `/:dateString` (a day's home page, /20261025) has the same shape as any
 * one-segment typo. Only a real YYYYMMDD date is a page; anything else is
 * `notFound`, rather than today's home page under a second address.
 */
export function DatePathOr({ notFound, children }: { notFound: ReactNode; children: ReactNode }) {
  const { dateString } = useParams<{ dateString?: string }>();
  return <>{dateString === undefined || parseDatePath(dateString) ? children : notFound}</>;
}
